<script setup>
import { ref, watch, onMounted } from 'vue'
import { useAuthStore } from '@/app/store/auth'
import { useStorage } from '@/core/composables/useStorage'
import { useToast } from '@/core/composables/useToast'
import { updateUserProfile } from '@/core/services/users'
import ImageUpload from '@ui/components/common/ImageUpload.vue'

const authStore = useAuthStore()
const { deleteImageByUrl } = useStorage()
const toast = useToast()

const showModal = ref(false)
const newAvatarUrl = ref('')
const saving = ref(false)

const STORAGE_KEY_PREFIX = 'gofood_hide_photo_prompt_'

function getStorageKey() {
  return STORAGE_KEY_PREFIX + authStore.userId
}

function shouldShowPrompt() {
  if (authStore.userPhotoURL) return false
  if (!authStore.userId) return false
  if (localStorage.getItem(getStorageKey())) return false
  return true
}

onMounted(() => {
  // Profile already loaded — decide immediately
  if (authStore.user.profile) {
    if (shouldShowPrompt()) {
      setTimeout(() => {
        showModal.value = true
      }, 1000)
    }
    return
  }

  // Profile not yet loaded — wait for it
  const stopWatch = watch(
    () => authStore.user.profile,
    (profile) => {
      if (!profile) return
      stopWatch()
      if (shouldShowPrompt()) {
        setTimeout(() => {
          showModal.value = true
        }, 1000)
      }
    },
    { immediate: true }
  )

  // Safety timeout — stop watching after 10s to avoid leaks
  setTimeout(() => {
    stopWatch()
  }, 10000)
})

async function savePhoto() {
  if (!newAvatarUrl.value || !authStore.userId) return

  saving.value = true
  try {
    const oldUrl = authStore.userPhotoURL
    if (oldUrl && oldUrl !== newAvatarUrl.value) {
      try {
        await deleteImageByUrl(oldUrl)
      } catch (err) {
        console.warn('Could not delete old avatar:', err)
      }
    }

    await updateUserProfile(authStore.userId, {
      photoURL: newAvatarUrl.value || null,
    })

    toast.success('תמונת הפרופיל עודכנה בהצלחה')
    showModal.value = false
  } catch (err) {
    console.error('Failed to save avatar:', err)
    toast.error('שגיאה בשמירת התמונה')
  } finally {
    saving.value = false
  }
}

function close() {
  showModal.value = false
}

function dontShowAgain() {
  localStorage.setItem(getStorageKey(), '1')
  showModal.value = false
}
</script>

<template>
  <Teleport to="body">
    <Transition name="prompt-fade">
      <div v-if="showModal" class="photo-prompt-overlay" @click.self="close">
        <div class="photo-prompt-card">
          <!-- Camera icon -->
          <div class="prompt-icon-wrapper">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="var(--bg-tertiary)" />
              <path
                d="M42 24h-3.17L37 22h-6l-1.83 2H26a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V26a2 2 0 00-2-2z"
                stroke="var(--color-primary)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <circle
                cx="34"
                cy="31"
                r="4"
                stroke="var(--color-primary)"
                stroke-width="2"
                fill="none"
              />
            </svg>
          </div>

          <!-- Text -->
          <h2 class="prompt-title">הוסיפו תמונת פרופיל!</h2>
          <p class="prompt-subtitle">תמונת פרופיל עוזרת ללקוחות ולצוות לזהות אתכם בקלות</p>

          <!-- Upload -->
          <div class="prompt-upload-area">
            <ImageUpload
              v-model="newAvatarUrl"
              folder="user-avatars"
              :file-name="authStore.userId"
            />
          </div>

          <!-- Actions -->
          <div class="prompt-actions">
            <button
              v-if="newAvatarUrl"
              class="prompt-btn-save"
              :disabled="saving"
              @click="savePhoto"
            >
              <span v-if="saving" class="btn-spinner"></span>
              <span v-else>שמור תמונה</span>
            </button>
            <button class="prompt-btn-close" @click="close">סגור</button>
            <button class="prompt-btn-dismiss" @click="dontShowAgain">לא להציג שוב</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ============================================
   Overlay
   ============================================ */
.photo-prompt-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
}

/* ============================================
   Card
   ============================================ */
.photo-prompt-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 420px;
  padding: 0 var(--spacing-xl) var(--spacing-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
}

/* ============================================
   Icon
   ============================================ */
.prompt-icon-wrapper {
  margin-top: var(--spacing-xl);
  margin-bottom: var(--spacing-md);
}

.prompt-icon-wrapper svg {
  width: 64px;
  height: 64px;
}

/* ============================================
   Text
   ============================================ */
.prompt-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.prompt-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-lg) 0;
  line-height: 1.5;
}

/* ============================================
   Upload area
   ============================================ */
.prompt-upload-area {
  width: 100%;
  max-width: 240px;
  margin-bottom: var(--spacing-lg);
}

.prompt-upload-area :deep(.upload-area) {
  aspect-ratio: 1;
  max-height: 200px;
  border-radius: var(--border-radius-lg);
}

.prompt-upload-area :deep(.image-preview img) {
  border-radius: var(--border-radius-lg);
}

/* ============================================
   Actions
   ============================================ */
.prompt-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
}

.prompt-btn-save {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}

.prompt-btn-save:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.prompt-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.prompt-btn-close {
  background: none;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-xs) var(--spacing-lg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  width: 67%;
  height: 50px;
}

.prompt-btn-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.prompt-btn-dismiss {
  background: none;
  border: none;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--spacing-xs);
  font-family: var(--font-family);
  transition: color var(--transition-fast);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.prompt-btn-dismiss:hover {
  color: var(--text-secondary);
}

/* ============================================
   Spinner
   ============================================ */
.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   Transition
   ============================================ */
.prompt-fade-enter-active {
  transition: opacity 0.3s ease;
}

.prompt-fade-leave-active {
  transition: opacity 0.2s ease;
}

.prompt-fade-enter-from,
.prompt-fade-leave-to {
  opacity: 0;
}

.prompt-fade-enter-active .photo-prompt-card {
  animation: card-enter 0.3s ease;
}

.prompt-fade-leave-active .photo-prompt-card {
  animation: card-leave 0.2s ease;
}

@keyframes card-enter {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes card-leave {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
}

/* ============================================
   Mobile: full-screen takeover
   ============================================ */
@media (max-width: 768px) {
  .photo-prompt-overlay {
    align-items: stretch;
    background: var(--bg-primary);
    backdrop-filter: none;
  }

  .photo-prompt-card {
    max-width: none;
    border-radius: 0;
    box-shadow: none;
    padding: 0 var(--spacing-lg) var(--spacing-lg);
    justify-content: center;
    flex: 1;
  }
}
</style>
